import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronRight, Download, File as FileIcon, FileText, Folder, FolderPlus, Loader2, Trash2, Upload, FolderUp, HardDrive, Search, Pencil, Check, X, FolderInput, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import ConfirmDialog from "@/components/ConfirmDialog";

const prettySize = (b) => {
  if (b === null || b === undefined) return "";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(0)} KB`;
  return `${(b / 1024 / 1024).toFixed(1)} MB`;
};
const blobFor = async (id) => (await api.get(`/drive/${id}/download`, { responseType: "blob" })).data;

function Thumb({ node }) {
  const ct = node.content_type || "";
  const isImg = ct.startsWith("image/");
  const isPdf = ct === "application/pdf";
  const [url, setUrl] = useState(null);
  useEffect(() => {
    if (node.is_folder || (!isImg && !isPdf)) return;
    let alive = true, u;
    blobFor(node.id).then((b) => { if (alive) { u = URL.createObjectURL(b); setUrl(u); } }).catch(() => {});
    return () => { alive = false; if (u) URL.revokeObjectURL(u); };
  }, [node.id, node.is_folder, isImg, isPdf]);

  if (node.is_folder) return <div className="h-10 w-10 shrink-0 rounded bg-amber-500/15 flex items-center justify-center"><Folder size={20} className="text-amber-400" /></div>;
  if (isImg && url) return <img src={url} alt="" className="h-10 w-10 shrink-0 rounded object-cover bg-secondary" data-testid={`files-thumb-img`} />;
  if (isPdf && url) return <object data={`${url}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} type="application/pdf" className="h-10 w-10 shrink-0 rounded overflow-hidden bg-white pointer-events-none" aria-label="PDF preview" />;
  return <div className="h-10 w-10 shrink-0 rounded bg-secondary flex items-center justify-center">{isPdf ? <FileText size={18} className="text-red-400" /> : <FileIcon size={18} className="text-cyan-400" />}</div>;
}

function FolderPicker({ open, onClose, onPick }) {
  const [pid, setPid] = useState(null);
  const [d, setD] = useState(null);
  useEffect(() => { if (open) { setPid(null); } }, [open]);
  useEffect(() => { if (!open) return; setD(null); api.get("/drive/list", { params: { parent_id: pid || undefined } }).then((r) => setD(r.data)).catch(() => {}); }, [open, pid]);
  const folders = (d?.nodes || []).filter((n) => n.is_folder);
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="files-move-dialog">
        <DialogHeader><DialogTitle>Move to…</DialogTitle></DialogHeader>
        <div className="flex items-center gap-1 text-sm flex-wrap">
          <button onClick={() => setPid(null)} className={`hover:text-primary ${!pid ? "text-primary font-medium" : "text-muted-foreground"}`}>Home</button>
          {(d?.breadcrumb || []).map((c) => (
            <span key={c.id} className="flex items-center gap-1"><ChevronRight size={13} className="text-muted-foreground" />
              <button onClick={() => setPid(c.id)} className={`hover:text-primary ${c.id === pid ? "text-primary font-medium" : "text-muted-foreground"}`}>{c.name}</button></span>
          ))}
        </div>
        <div className="border rounded-lg max-h-64 overflow-y-auto divide-y min-h-[120px]">
          {!d && <div className="p-6 text-center"><Loader2 className="animate-spin text-primary mx-auto" /></div>}
          {d && folders.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No subfolders here.</p>}
          {folders.map((f) => (
            <button key={f.id} onClick={() => setPid(f.id)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary/50" data-testid={`files-move-folder-${f.id}`}>
              <Folder size={16} className="text-amber-400" /> <span className="truncate flex-1">{f.name}</span><ChevronRight size={14} className="text-muted-foreground" />
            </button>
          ))}
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={() => onPick(pid)} data-testid="files-move-here-button">Move here {pid ? `(${d?.folder?.name || "folder"})` : "(Home)"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function CustomerPicker({ open, onClose, onPick }) {
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  useEffect(() => { if (open) { setQ(""); setList(null); api.get("/customers").then((r) => setList(r.data)).catch((e) => toast.error(formatError(e))); } }, [open]);
  const filtered = (list || []).filter((c) => c.name.toLowerCase().includes(q.trim().toLowerCase()));
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-card" data-testid="files-customer-dialog">
        <DialogHeader><DialogTitle>Copy to customer folder</DialogTitle></DialogHeader>
        <Input autoFocus placeholder="Search customers…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="files-customer-search" />
        <div className="border rounded-lg max-h-72 overflow-y-auto divide-y min-h-[120px]">
          {!list && <div className="p-6 text-center"><Loader2 className="animate-spin text-primary mx-auto" /></div>}
          {list && filtered.length === 0 && <p className="p-6 text-center text-sm text-muted-foreground">No customers found.</p>}
          {filtered.map((c) => (
            <button key={c.key} onClick={() => onPick(c)} className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm hover:bg-secondary/50" data-testid={`files-customer-${c.key}`}>
              <span className="truncate flex-1 font-medium">{c.name}</span>
              <span className="text-xs text-muted-foreground">{c.file_count} file{c.file_count === 1 ? "" : "s"}</span>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function FilesManager() {
  const [parentId, setParentId] = useState(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(null);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);
  const [deleteTargets, setDeleteTargets] = useState(null);
  const [search, setSearch] = useState("");
  const [results, setResults] = useState(null);
  const [renameId, setRenameId] = useState(null);
  const [renameVal, setRenameVal] = useState("");
  const [dragId, setDragId] = useState(null);
  const [dropTarget, setDropTarget] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [moveOpen, setMoveOpen] = useState(false);
  const [custTargets, setCustTargets] = useState(null);
  const fileInput = useRef(null);
  const folderInput = useRef(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/drive/list", { params: { parent_id: parentId || undefined } })
      .then((r) => setData(r.data)).catch((e) => toast.error(formatError(e))).finally(() => setLoading(false));
  }, [parentId]);
  useEffect(() => { load(); setSelected(new Set()); }, [load]);

  useEffect(() => {
    const term = search.trim();
    if (!term) { setResults(null); return; }
    const t = setTimeout(() => api.get("/drive/search", { params: { q: term } }).then((r) => setResults(r.data.results)).catch(() => setResults([])), 300);
    return () => clearTimeout(t);
  }, [search]);

  const showingSearch = results !== null;
  const nodes = showingSearch ? results : (data?.nodes || []);
  const selNodes = nodes.filter((n) => selected.has(n.id));

  const toggleSel = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const createFolder = async () => {
    const name = newName.trim();
    if (!name) return;
    setBusy(true);
    try { await api.post("/drive/folder", { name, parent_id: parentId }); setNewName(""); setCreating(false); load(); }
    catch (e) { toast.error(formatError(e)); }
    setBusy(false);
  };

  const uploadFiles = async (files) => {
    const list = [...files];
    if (!list.length) return;
    setBusy(true);
    let ok = 0;
    for (let i = 0; i < list.length; i++) {
      const f = list[i];
      setProgress(`Uploading ${i + 1} of ${list.length}…`);
      const form = new FormData();
      form.append("file", f, f.name);
      if (parentId) form.append("parent_id", parentId);
      const rel = f.webkitRelativePath || "";
      const dir = rel.includes("/") ? rel.slice(0, rel.lastIndexOf("/")) : "";
      if (dir) form.append("rel_path", dir);
      try { await api.post("/drive/upload", form); ok += 1; } catch (e) { toast.error(`${f.name}: ${formatError(e)}`); }
    }
    setProgress(null); setBusy(false);
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} uploaded`);
    load();
  };

  const openFile = async (node) => { try { const url = URL.createObjectURL(await blobFor(node.id)); window.open(url, "_blank"); setTimeout(() => URL.revokeObjectURL(url), 60000); } catch (e) { toast.error(formatError(e)); } };
  const downloadOne = async (node) => {
    try { const url = URL.createObjectURL(await blobFor(node.id)); const a = document.createElement("a"); a.href = url; a.download = node.original_filename || node.name; document.body.appendChild(a); a.click(); a.remove(); setTimeout(() => URL.revokeObjectURL(url), 60000); }
    catch (e) { toast.error(formatError(e)); }
  };
  const downloadFolder = async (node) => {
    setBusy(true); setProgress(`Zipping “${node.name}”…`);
    try {
      const { data } = await api.get(`/drive/${node.id}/download-zip`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      const a = document.createElement("a"); a.href = url; a.download = `${node.name}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatError(e)); }
    setProgress(null); setBusy(false);
  };
  const downloadSelected = async () => { for (const n of selNodes) { if (n.is_folder) await downloadFolder(n); else await downloadOne(n); } };

  const confirmDelete = async () => {
    const ids = deleteTargets.map((n) => n.id);
    try {
      if (ids.length === 1) await api.delete(`/drive/${ids[0]}`); else await api.post("/drive/bulk-delete", { ids });
      toast.success(`${ids.length} item${ids.length === 1 ? "" : "s"} deleted`);
      setSelected(new Set());
      showingSearch ? setResults((rs) => rs.filter((x) => !ids.includes(x.id))) : load();
    } catch (e) { toast.error(formatError(e)); }
    setDeleteTargets(null);
  };

  const startRename = (n) => { setRenameId(n.id); setRenameVal(n.name); };
  const saveRename = async (n) => {
    const name = renameVal.trim();
    if (!name || name === n.name) { setRenameId(null); return; }
    try { await api.patch(`/drive/${n.id}`, { name }); setRenameId(null); showingSearch ? setResults((rs) => rs.map((x) => x.id === n.id ? { ...x, name } : x)) : load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const moveNode = async (id, targetId) => {
    if (!id || id === targetId) return;
    try { await api.post(`/drive/${id}/move`, { parent_id: targetId }); toast.success("Moved"); load(); }
    catch (e) { toast.error(formatError(e)); }
    setDragId(null); setDropTarget(null);
  };
  const doBulkMove = async (targetId) => {
    setMoveOpen(false);
    try { const { data } = await api.post("/drive/bulk-move", { ids: [...selected], parent_id: targetId }); toast.success(`${data.moved} moved`); setSelected(new Set()); load(); }
    catch (e) { toast.error(formatError(e)); }
  };

  const copyToCustomer = async (customer) => {
    const files = custTargets.filter((n) => !n.is_folder);
    setCustTargets(null);
    setBusy(true);
    let ok = 0;
    for (const n of files) { try { await api.post(`/customers/${encodeURIComponent(customer.key)}/files/from-drive`, { node_id: n.id }); ok += 1; } catch (e) { toast.error(`${n.name}: ${formatError(e)}`); } }
    setBusy(false);
    if (ok) toast.success(`${ok} file${ok === 1 ? "" : "s"} copied to ${customer.name}`);
    setSelected(new Set());
  };

  const crumbs = data?.breadcrumb || [];

  const Row = ({ n, i, searchRow }) => (
    <div
      draggable={!searchRow && renameId !== n.id}
      onDragStart={() => setDragId(n.id)}
      onDragEnd={() => { setDragId(null); setDropTarget(null); }}
      onDragOver={(e) => { if (!searchRow && n.is_folder && dragId && dragId !== n.id) { e.preventDefault(); setDropTarget(n.id); } }}
      onDragLeave={() => { if (dropTarget === n.id) setDropTarget(null); }}
      onDrop={(e) => { if (!searchRow && n.is_folder) { e.preventDefault(); moveNode(dragId, n.id); } }}
      className={`flex items-center gap-3 px-3 py-2.5 transition-colors group ${dropTarget === n.id ? "bg-primary/20 ring-1 ring-primary" : selected.has(n.id) ? "bg-primary/10" : "hover:bg-secondary/40"}`}
      data-testid={searchRow ? `files-result-${i}` : `files-node-${i}`}>
      <Checkbox checked={selected.has(n.id)} onCheckedChange={() => toggleSel(n.id)} data-testid={`files-select-${i}`} aria-label="Select" />
      <Thumb node={n} />
      {renameId === n.id ? (
        <div className="flex items-center gap-2 flex-1">
          <Input autoFocus value={renameVal} onChange={(e) => setRenameVal(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") saveRename(n); if (e.key === "Escape") setRenameId(null); }} className="h-8 max-w-xs" data-testid={`files-rename-input-${i}`} />
          <button onClick={() => saveRename(n)} className="text-emerald-400" data-testid={`files-rename-save-${i}`}><Check size={16} /></button>
          <button onClick={() => setRenameId(null)} className="text-muted-foreground"><X size={16} /></button>
        </div>
      ) : (
        <>
          <button onClick={() => n.is_folder ? (searchRow ? (setSearch(""), setParentId(n.id)) : setParentId(n.id)) : openFile(n)} className="flex items-center gap-3 min-w-0 flex-1 text-left" data-testid={searchRow ? `files-result-open-${i}` : `files-open-${i}`}>
            <span className="truncate font-medium">{n.name}</span>
            {searchRow && <span className="truncate text-xs text-muted-foreground">in /{(n.path || []).join("/") || ""}</span>}
            <span className="ml-auto text-xs text-muted-foreground shrink-0">{n.is_folder ? "Folder" : prettySize(n.size)}</span>
          </button>
          <button onClick={() => startRename(n)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition" title="Rename" data-testid={`files-rename-${i}`}><Pencil size={15} /></button>
          {!n.is_folder && <button onClick={() => setCustTargets([n])} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition" title="Copy to customer folder" data-testid={`files-tocust-${i}`}><UserPlus size={15} /></button>}
          {n.is_folder && <button onClick={() => downloadFolder(n)} disabled={busy} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition disabled:opacity-40" title="Download folder (.zip)" data-testid={`files-download-folder-${i}`}><Download size={16} /></button>}
          {!n.is_folder && <button onClick={() => downloadOne(n)} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-primary transition" title="Download" data-testid={`files-download-${i}`}><Download size={16} /></button>}
          <button onClick={() => setDeleteTargets([n])} className="opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-red-400 transition" title="Delete" data-testid={`files-delete-${i}`}><Trash2 size={16} /></button>
        </>
      )}
    </div>
  );

  return (
    <div className="p-6 max-w-6xl mx-auto" data-testid="files-manager">
      <input ref={fileInput} type="file" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="files-file-input" />
      <input ref={folderInput} type="file" webkitdirectory="" directory="" multiple className="hidden" onChange={(e) => { uploadFiles(e.target.files); e.target.value = ""; }} data-testid="files-folder-input" />

      <div className="flex items-center gap-3 mb-5">
        <div className="h-10 w-10 rounded-lg bg-primary/15 text-primary flex items-center justify-center"><HardDrive size={20} /></div>
        <div>
          <h1 className="font-display text-2xl font-bold" data-testid="files-title">Files</h1>
          <p className="text-sm text-muted-foreground">Create folders, drag to move, and upload files to share with your team.</p>
        </div>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setCreating(true)} data-testid="files-new-folder-button"><FolderPlus size={15} /> New folder</Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => fileInput.current.click()} data-testid="files-upload-button"><Upload size={15} /> Upload files</Button>
          <Button variant="outline" size="sm" disabled={busy} onClick={() => folderInput.current.click()} data-testid="files-upload-folder-button"><FolderUp size={15} /> Upload folder</Button>
        </div>
      </div>

      <div className="relative mb-4 max-w-md">
        <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input placeholder="Search all files and folders…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="files-search-input" />
        {search && <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" data-testid="files-search-clear"><X size={15} /></button>}
      </div>

      {!showingSearch && (
        <div className="flex items-center gap-1 text-sm mb-3 flex-wrap" data-testid="files-breadcrumb">
          <button onClick={() => setParentId(null)} onDragOver={(e) => { if (dragId) { e.preventDefault(); setDropTarget("root"); } }} onDrop={(e) => { e.preventDefault(); moveNode(dragId, null); }}
            className={`px-1.5 py-0.5 rounded hover:text-primary transition-colors ${dropTarget === "root" ? "bg-primary/20 ring-1 ring-primary" : ""} ${!parentId ? "text-primary font-medium" : "text-muted-foreground"}`} data-testid="files-crumb-root">Home</button>
          {crumbs.map((c) => (
            <span key={c.id} className="flex items-center gap-1"><ChevronRight size={14} className="text-muted-foreground" />
              <button onClick={() => setParentId(c.id)} onDragOver={(e) => { if (dragId) { e.preventDefault(); setDropTarget(c.id); } }} onDrop={(e) => { e.preventDefault(); moveNode(dragId, c.id); }}
                className={`px-1.5 py-0.5 rounded hover:text-primary transition-colors ${dropTarget === c.id ? "bg-primary/20 ring-1 ring-primary" : ""} ${c.id === parentId ? "text-primary font-medium" : "text-muted-foreground"}`} data-testid={`files-crumb-${c.id}`}>{c.name}</button></span>
          ))}
        </div>
      )}

      {creating && !showingSearch && (
        <div className="flex items-center gap-2 mb-3 p-3 border rounded-lg bg-card" data-testid="files-new-folder-form">
          <Folder size={16} className="text-primary" />
          <Input autoFocus placeholder="Folder name" value={newName} onChange={(e) => setNewName(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} className="h-8 max-w-xs" data-testid="files-new-folder-name" />
          <Button size="sm" disabled={busy || !newName.trim()} onClick={createFolder} data-testid="files-new-folder-save">Create</Button>
          <Button size="sm" variant="ghost" onClick={() => { setCreating(false); setNewName(""); }}>Cancel</Button>
        </div>
      )}

      {selected.size > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2" data-testid="files-selection-bar">
          <span className="text-sm font-medium" data-testid="files-selected-count">{selected.size} selected</span>
          <Button size="sm" variant="outline" disabled={busy} onClick={downloadSelected} data-testid="files-bulk-download"><Download size={15} /> Download</Button>
          <Button size="sm" variant="outline" disabled={busy} onClick={() => setMoveOpen(true)} data-testid="files-bulk-move"><FolderInput size={15} /> Move</Button>
          <Button size="sm" variant="outline" disabled={busy || !selNodes.some((n) => !n.is_folder)} onClick={() => setCustTargets(selNodes)} data-testid="files-bulk-tocust"><UserPlus size={15} /> Copy to customer</Button>
          <Button size="sm" variant="outline" className="text-red-400 hover:bg-red-500/10 hover:text-red-400" disabled={busy} onClick={() => setDeleteTargets(selNodes)} data-testid="files-bulk-delete"><Trash2 size={15} /> Delete</Button>
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())}>Clear</Button>
        </div>
      )}

      {progress && <p className="text-sm text-primary mb-2 flex items-center gap-2" data-testid="files-progress"><Loader2 size={14} className="animate-spin" /> {progress}</p>}

      <div className="border rounded-lg overflow-hidden bg-card min-h-[280px] relative" data-testid={showingSearch ? "files-search-results" : "files-list"}>
        {loading && !showingSearch && <div className="absolute inset-0 bg-background/40 flex items-center justify-center z-10"><Loader2 className="animate-spin text-primary" /></div>}
        {nodes.length === 0 && !loading && (
          <div className="p-16 text-center text-muted-foreground" data-testid={showingSearch ? "files-search-empty" : "files-empty"}>
            <Folder size={36} className="mx-auto mb-3 opacity-40" />
            {showingSearch ? `No files or folders match “${search.trim()}”.` : "This folder is empty. Create a folder or upload files to get started."}
          </div>
        )}
        <div className="divide-y">{nodes.map((n, i) => <Row key={n.id} n={n} i={i} searchRow={showingSearch} />)}</div>
      </div>

      <FolderPicker open={moveOpen} onClose={() => setMoveOpen(false)} onPick={doBulkMove} />
      <CustomerPicker open={!!custTargets} onClose={() => setCustTargets(null)} onPick={copyToCustomer} />
      <ConfirmDialog open={!!deleteTargets} onOpenChange={(o) => !o && setDeleteTargets(null)} onConfirm={confirmDelete}
        title={`Delete ${deleteTargets?.length === 1 ? (deleteTargets[0].is_folder ? "folder" : "file") : `${deleteTargets?.length} items`}?`}
        description={deleteTargets?.some((n) => n.is_folder) ? "Folders and everything inside them will be removed." : "The selected files will be removed."}
        testId="files-delete" confirmLabel="Delete" />
    </div>
  );
}
